import CalcRecommended, { PlanData } from "./CalcRecommended"
import { CUSTOM_RATE_THRESHOLD, RATE_THRESHOLD } from "./constants"
import RecommendedPlanConfig from "./RecommendedPlanConfig"
import { Color } from "../../../../constants/V2/color"
import Grid from "../../../elements/V2/Grid"
import LayoutItem from "../../Layouts/LayoutItem"

import React, { ComponentProps, useCallback, useEffect, useState } from "react"

export type RecommendedPlanConfigs = ComponentProps<
  typeof RecommendedPlanConfig
> & {
  maxSliderRange: number
  minSliderRange: number
  tracking?: {
    event?: string
    eventKey?: string
    eventValue?: string | number
  }
}

type RecommendedSliderProps = {
  sliderTitle: string
  sliderDescription: string
  sliderMinRange: number
  sliderMaxRange: number
  sliderInitialValue?: number
  sliderBackgroundColor: Color
  recommendedPlanConfigs?: RecommendedPlanConfigs[]
  mobileGridOrder?: number
}

const RecommendedSlider = ({
  sliderTitle,
  sliderDescription,
  sliderMinRange,
  sliderMaxRange,
  sliderInitialValue = 0,
  sliderBackgroundColor,
  recommendedPlanConfigs,
  mobileGridOrder,
  ...props
}: RecommendedSliderProps) => {
  const [recommendedPlanConfigIndex, setRecommendedPlanConfigIndex] =
    useState<number>(-1)

  const [
    recommendedPlanTransactionAmount,
    setRecommendedPlanTransactionAmount,
  ] = useState<number | string>(0)

  // This is done to determine what the initial recommended plan should be based on the sliders initial value
  useEffect(() => {
    onSliderChange(sliderInitialValue)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSliderChange = useCallback(
    (value: number) => {
      if (!recommendedPlanConfigs || recommendedPlanConfigs.length === 0) {
        setRecommendedPlanConfigIndex(-1)
        return
      }

      const recommendedPlanConfigIndex = recommendedPlanConfigs.findIndex(
        (recommendedPlanConfig) => {
          const { minSliderRange, maxSliderRange } = recommendedPlanConfig
          return value >= minSliderRange && value <= maxSliderRange
        }
      )

      setRecommendedPlanConfigIndex(recommendedPlanConfigIndex)
      const recommendedPlan =
        recommendedPlanConfigs[recommendedPlanConfigIndex].planConfig

      setRecommendedPlanTransactionAmount(
        value <= RATE_THRESHOLD
          ? recommendedPlan?.standardRate
          : value >= CUSTOM_RATE_THRESHOLD
            ? "custom"
            : recommendedPlan.reducedRate || recommendedPlan.standardRate
      )
    },
    [recommendedPlanConfigs]
  )

  const activePlanConfig = recommendedPlanConfigs?.[recommendedPlanConfigIndex]

  return (
    <LayoutItem
      as="div"
      mobileGridOrder={mobileGridOrder}
      className="col-span-full"
      {...props}
    >
      {/* Capped and centred so screens wider than the design keep the design
       * layout instead of the calculator and card drifting to opposite edges
       */}
      <Grid className="mx-auto max-w-[1232px]">
        <CalcRecommended
          title={sliderTitle}
          description={sliderDescription}
          minRange={sliderMinRange}
          maxRange={sliderMaxRange}
          initialSliderValue={sliderInitialValue}
          backgroundColor={sliderBackgroundColor}
          onSliderChange={onSliderChange}
          plans={
            recommendedPlanConfigs?.map(
              (recommendedPlanConfig): PlanData => ({
                title: recommendedPlanConfig.planConfig.title ?? "",
                fromAmount: recommendedPlanConfig.planConfig.fromAmount ?? "",
                standardAmount: recommendedPlanConfig.planConfig.standardRate,
                reducedAmount:
                  recommendedPlanConfig.planConfig.reducedRate ??
                  recommendedPlanConfig.planConfig.standardRate,
                payoutDescription:
                  recommendedPlanConfig.planConfig.payoutDescription,
              })
            ) || []
          }
          recommendedPlanTransactionAmount={recommendedPlanTransactionAmount.toString()}
        />
        {recommendedPlanConfigIndex !== -1 && activePlanConfig && (
          <RecommendedPlanConfig
            planConfig={activePlanConfig.planConfig}
            ctaText={activePlanConfig.ctaText}
            ctaLink={activePlanConfig.ctaLink}
            ctaColor={activePlanConfig.ctaColor}
            testimonialTitle={activePlanConfig.testimonialTitle}
            testimonialQuote={activePlanConfig.testimonialQuote}
            testimonialImage={activePlanConfig.testimonialImage}
            testimonialName={activePlanConfig.testimonialName}
            testimonialBusiness={activePlanConfig.testimonialBusiness}
            tracking={activePlanConfig.tracking}
            key={`recommended-plan-config-${recommendedPlanConfigIndex}`}
          />
        )}
      </Grid>
    </LayoutItem>
  )
}

export default RecommendedSlider
